import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { safeFetch, safeAuthFetch } from '../utils/safeFetch'
import { API_BASE_URL } from '../config/api'
import { useBranding } from '../contexts/BrandingContext'
import AuthoringDemoDialog from '../components/LazyAuthoringDemoDialog' // #4231: dialog chunk fetched on first open
import CategoryIconCards from '../components/CategoryIconCards'
import DigCompWheel from '../components/DigCompWheel'
import {
  HUMAN_OVERSIGHT_ANCHOR,
  HumanOversightSection,
  DigCompHomeHeader,
  HowWeMapSection,
  FromCopSection,
  DigCompContactSection,
} from '../components/digcomp/DigCompCopSections'
import LoginModal from '../components/LoginModal'
import GuestWelcomeHero from '../components/guestDiscovery/GuestWelcomeHero'
import GuestHomeHeader from '../components/guestDiscovery/GuestHomeHeader'
import CommitmentSheetOverlay from '../components/guestDiscovery/CommitmentSheetOverlay'
import LastRunContinuity from '../components/guestDiscovery/LastRunContinuity'
import { posthog } from '../main'
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_GROUPS, getPreferredLanguage, setPreferredLanguage, getLanguageConfig } from '../lib/voice/languageConfig'
import { useWheelScrollPassthrough } from '../hooks/useWheelScrollPassthrough'
import { readLastRun } from '../utils/lastRunContinuity'
import { documentTitleFor } from '../lib/siteMeta'
import { shouldEnsureGuestSession } from '../lib/auth/guestOrgMismatch'
import { guestStartErrorMessage } from '../lib/discovery/guestStartErrorMessage'
import './HomePageV2.css'

function HomePageV2() {
  const navigate = useNavigate()
  const location = useLocation()
  const { audience: audienceSlug } = useParams() // From /for/:audience route
  // #4724: /for/digcomp gets its own hero copy and DigComp CoP sections in
  // place of the generic features / B2B blocks. Keyed on the URL slug (known
  // synchronously), not the async audience config, so the #human-oversight
  // anchor and everything above it render on the first paint.
  const isDigComp = audienceSlug === 'digcomp'
  // `isLoading: authLoading` (aliased — this component already has its own
  // scenario-fetch `isLoading` local state below): #1926 round-2 review-fix.
  // AuthContext starts `user` at `null` and resolves it asynchronously
  // (AuthContext.jsx initAuth), so on a hard load `user?.is_guest` reads
  // `undefined` for one or more renders before the real value is known. The
  // hash-scroll effect below must not run its is_guest decision on that
  // transient null read — see its own comment.
  const { isAuthenticated, user, loginAsGuest, isLoading: authLoading } = useAuth()
  const { t, i18n } = useTranslation()
  const branding = useBranding()
  const [orgBranding, setOrgBranding] = useState(null)

  // State
  const [scenarios, setScenarios] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [audienceConfig, setAudienceConfig] = useState(null)
  const [startingScenarioId, setStartingScenarioId] = useState(null)
  const [startError, setStartError] = useState(null)
  // #4724: true when the scenario-preview fetch failed (vs. returned no oversight scenarios).
  const [loadError, setLoadError] = useState(false)
  // #2335: the scenario the commitment sheet is showing (null = sheet closed).
  const [committingScenario, setCommittingScenario] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(() => getPreferredLanguage()) // Issue #435, #496
  const [showAuthoringDialog, setShowAuthoringDialog] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(() => {
    try {
      return localStorage.getItem('homepage_selected_category') || 'all'
    } catch {
      return 'all'
    }
  })
  const [selectedFrameworkArea, setSelectedFrameworkArea] = useState(null)
  // Issue #1926 (J19, D-080 re-scope): read once at mount — this page always
  // does a full route-level remount on a guest's `navigate(guestHomeUrl)`
  // exit (ChatPage.jsx and HomePageV2.jsx are different route components),
  // so a mount-time read is sufficient; no need to re-check on an interval.
  const [lastRun] = useState(() => readLastRun())
  // #4724: learning-outcome map (lane B's generated JSON), fetched as its own
  // chunk only on /for/digcomp so other pages never download it.
  const [digcompOutcomeMap, setDigcompOutcomeMap] = useState(null)
  useEffect(() => {
    if (!isDigComp) return undefined
    let cancelled = false
    import('../data/digcompOutcomeMap.json')
      .then((mod) => { if (!cancelled) setDigcompOutcomeMap(mod.default || mod) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [isDigComp])

  // Refs for scroll handling
  const scenariosScrollRef = useRef(null)
  const featuresGridRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // #1842: forward vertical wheel intent out of the horizontal strips so
  // page scroll isn't trapped while the pointer hovers them.
  useWheelScrollPassthrough([scenariosScrollRef, featuresGridRef], [isLoading, scenarios, selectedCategory])

  // Intersection Observer for scroll-triggered animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    )

    const elements = document.querySelectorAll('.v2-fade-up, .v2-scale-up')
    elements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [isLoading]) // Re-run when loading completes

  // Handle hash scrolling
  //
  // #1926 review-fix (P1): `guestHomeUrl` in ChatPage.jsx always appends
  // `#scenarios` on every guest exit (both `/for/<audience>#scenarios` and
  // `/#scenarios`), and this effect fires on mount before the guest ever
  // sees the page. On the audience path the `#scenarios` target exists and
  // sits below the LastRunContinuity block (line ~430 below), so the
  // unconditional scroll carried the "welcome back" message off-screen the
  // instant the page mounted -- the message rendered, then was immediately
  // scrolled past, so no guest could ever see it on the return path it
  // exists for. LastRunContinuity itself renders under exactly
  // `user?.is_guest && lastRun` (mirrored below at its mount site), so
  // suppressing the auto-scroll under that same condition leaves the
  // welcome-back message the first thing in view; the guest can still
  // reach the scenarios gallery via the block's own "Browse more
  // scenarios" CTA (handleContinuityBrowse) or any other #scenarios link
  // on the page.
  //
  // #1926 round-2 review-fix (P1): the guard above only works once auth has
  // resolved. AuthContext (AuthContext.jsx) starts `user` at `null` and sets
  // the real value asynchronously from `initAuth`, so on a hard load of
  // `/for/:audience#scenarios` this effect's FIRST run sees `user === null`
  // -> `user?.is_guest` reads `undefined`, the guard above is false, and the
  // unconditional scroll below fires immediately -- before the guest's
  // identity, and therefore whether they qualify for the continuity block,
  // is even known. By the time `user?.is_guest` resolves to `true` and the
  // effect re-runs (it is a dependency), the smooth scroll has already
  // carried the page past the continuity block; a later no-op return does
  // not undo an already-completed scroll. Same defect class, same fix shape
  // as ScenarioDetailPage.jsx's own authLoading-return early-exit: hold
  // every hash-scroll decision until auth settles, so the is_guest/lastRun
  // guard below is only ever evaluated against a resolved identity.
  //
  // #4724: `#human-oversight` is an explicit inbound link (a DigComp CoP
  // forum post), never the guest-exit `#scenarios` the continuity guard
  // exists for, so it always scrolls. Its section sits directly under the
  // static hero and renders before any data loads, so one scroll is enough.
  //
  // #4724 review-fix: (1) the guard bypass is gated on isDigComp, so another
  // audience page keeps the continuity guard even for a hand-typed
  // `#human-oversight`; (2) the landing decision is made ONCE per hash
  // (`landedHashRef`) — later `user?.is_guest` / `lastRun` changes must not
  // re-scroll a reader who has moved on; (3) content ABOVE the anchor that
  // arrives late (the "Powered by" line from the org-branding fetch, the
  // branding context) shifts it, so the landing is re-applied once per such
  // arrival, but only while the reader has not scrolled since load
  // (`userScrolledRef`, set by wheel / touch / key / pointer input).
  const isOversightLink = isDigComp && location.hash === `#${HUMAN_OVERSIGHT_ANCHOR}`
  const landedHashRef = useRef(null)
  const landedKeyRef = useRef(null)
  const userScrolledRef = useRef(false)
  useEffect(() => {
    const markScrolled = () => { userScrolledRef.current = true }
    const events = ['wheel', 'touchmove', 'keydown', 'mousedown']
    events.forEach((e) => window.addEventListener(e, markScrolled, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, markScrolled))
  }, [])

  // #4724: the scenario wheel now sits above the anchor, so its arrival (loading -> N scenarios) is late content too.
  const lateContentKey = `${orgBranding?.powered_by || ''}|${branding.poweredBy || ''}|${audienceConfig ? 1 : 0}|${isLoading ? 'loading' : scenarios.length}`
  useEffect(() => {
    if (authLoading || !location.hash) return
    if (landedHashRef.current === location.hash) return
    landedHashRef.current = location.hash
    landedKeyRef.current = lateContentKey
    userScrolledRef.current = false
    if (!isOversightLink && user?.is_guest && lastRun) return
    const element = document.getElementById(location.hash.slice(1))
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    // user?.is_guest / lastRun / lateContentKey are read once at landing on purpose (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, authLoading, isOversightLink])

  useEffect(() => {
    if (!isOversightLink || authLoading) return
    if (landedHashRef.current !== location.hash || landedKeyRef.current === lateContentKey) return
    landedKeyRef.current = lateContentKey
    if (userScrolledRef.current) return
    // Instant, not smooth: this corrects a drift, it is not a second animation.
    document.getElementById(HUMAN_OVERSIGHT_ANCHOR)?.scrollIntoView({ behavior: 'auto' })
  }, [lateContentKey, isOversightLink, authLoading, location.hash])

  // #4724: state changes are not the only late content above the anchor. Wheel avatars, fonts and the
  // framework SVG resize the page after the scenarios arrive, with no state change to key on. For the
  // first seconds after landing, re-apply the landing on any resize of the content above the anchor,
  // until the reader scrolls.
  useEffect(() => {
    if (!isOversightLink || authLoading || typeof ResizeObserver === 'undefined') return undefined
    const above = document.getElementById('scenarios')
    if (!above) return undefined
    const until = Date.now() + 8000
    const ro = new ResizeObserver(() => {
      if (userScrolledRef.current || Date.now() > until) { ro.disconnect(); return }
      document.getElementById(HUMAN_OVERSIGHT_ANCHOR)?.scrollIntoView({ behavior: 'auto' })
    })
    ro.observe(above)
    const stop = setTimeout(() => ro.disconnect(), 8000)
    return () => { clearTimeout(stop); ro.disconnect() }
  }, [isOversightLink, authLoading])

  // Scenario deep-link: /for/<audience>#scenario-<id> (shareable card link).
  // Cards render inside the framework wheel's per-area detail, so the target
  // area must be selected before the card exists in the DOM; the wheel then
  // scrolls to and highlights the card itself.
  const deepLinkScenarioId = useMemo(() => {
    const m = /^#scenario-(\d+)$/.exec(location.hash)
    return m ? Number(m[1]) : null
  }, [location.hash])

  useEffect(() => {
    if (!deepLinkScenarioId || isLoading) return
    const target = scenarios.find(s => s.id === deepLinkScenarioId)
    if (target?.competence_code) {
      setSelectedFrameworkArea(target.competence_code.split('.')[0])
    }
  }, [deepLinkScenarioId, isLoading, scenarios])

  // Fetch org branding for audience pages (Issue #514)
  useEffect(() => {
    if (!audienceSlug) return;
    // Public endpoint; 404 when audience has no custom branding — treat as no-op.
    safeFetch(`${API_BASE_URL}/auth/org-branding/${encodeURIComponent(audienceSlug)}`, {
      allowErrorStatuses: [404],
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.branding) setOrgBranding(data.branding); })
      .catch(() => {});
  }, [audienceSlug]);

  // Brand name: audience config name > org branding > context branding > "Tacit"
  // Used for the hero label -- on an audience page this is a topic/partner
  // label (e.g. "DigComp", "Healthcare", "UNICEF"), not a product name.
  const brandName = audienceConfig?.name || orgBranding?.app_name || branding.appName;
  // Product brand name: deliberately excludes audienceConfig.name -- "Why
  // DigComp?" / "Why Healthcare?" don't make sense as a features-section
  // question, it should always ask about the product (Tacit, or the org's
  // white-label name when one applies).
  const productBrandName = orgBranding?.app_name || branding.appName;

  // #3756: the branded tab title (Tacit's public title only on Tacit-default
  // branding), so a white-labelled user on /for/:audience keeps their org's.
  const brandedTitle = documentTitleFor(branding)

  // Fetch audience config + scenarios (Issue #393)
  useEffect(() => {
    const defaultTitle = brandedTitle
    // Set in cleanup: a slow audience-config fetch must not write the title or
    // description after the page has been left (it would stick on later pages).
    let cancelled = false
    // Audience pages overwrite the meta description below; remember the
    // static one so leaving the page restores it (as the title already was).
    const metaDescEl = document.querySelector('meta[name="description"]')
    const defaultDescription = metaDescEl?.getAttribute('content')

    const loadData = async () => {
      // Reset state when audience slug or language changes
      setIsLoading(true)
      setAudienceConfig(null)
      // Only reset category for audience pages; for default homepage, preserve
      // the localStorage-initialized value so guests return to their last category
      if (audienceSlug) {
        setSelectedCategory('all')
      }

      try {
        // If an audience slug is present, fetch its config
        if (audienceSlug) {
          try {
            // 404 is expected when the audience slug has no saved config — treat as "no custom config, use defaults".
            const configRes = await safeFetch(
              `${API_BASE_URL}/api/demo/audience/${encodeURIComponent(audienceSlug)}`,
              { allowErrorStatuses: [404] }
            )
            if (configRes.ok) {
              const configData = await configRes.json()
              if (!cancelled && configData.success && configData.audience) {
                setAudienceConfig(configData.audience)

                // Set default category if specified in audience config
                if (configData.audience.default_category) {
                  setSelectedCategory(configData.audience.default_category)
                }

                // Set SEO meta tags from audience config
                if (configData.audience.meta_title) {
                  document.title = configData.audience.meta_title
                }
                if (configData.audience.meta_description) {
                  const metaDesc = document.querySelector('meta[name="description"]')
                  if (metaDesc) {
                    metaDesc.setAttribute('content', configData.audience.meta_description)
                  }
                }
              }
            }
            // If 404, audienceConfig stays null -- will use defaults
          } catch (_err) {
            // Config fetch failed; proceed with defaults
            console.error('Failed to load audience config:', _err)
          }
        } else {
          // Reset to default title when on homepage
          document.title = defaultTitle
        }

        // Load scenarios (pass audience slug for server-side filtering)
        // Issue #508: Pass language for DB-backed translations
        const langParam = selectedLanguage && selectedLanguage !== DEFAULT_LANGUAGE ? selectedLanguage : null
        const params = new URLSearchParams()
        if (audienceSlug) params.set('audience', audienceSlug)
        if (langParam) params.set('lang', langParam)
        const scenarioUrl = `${API_BASE_URL}/api/demo/scenarios/preview${params.toString() ? '?' + params.toString() : ''}`
        const response = await safeFetch(scenarioUrl)
        const data = await response.json()
        if (data.success) {
          setScenarios(data.scenarios)
          setLoadError(false)
        } else {
          setLoadError(true)
        }
      } catch (err) {
        console.error('Failed to load demo scenarios:', err)
        setLoadError(true)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()

    // Cleanup: restore default title when navigating away
    return () => {
      cancelled = true
      document.title = defaultTitle
      if (metaDescEl && defaultDescription) metaDescEl.setAttribute('content', defaultDescription)
    }
  }, [audienceSlug, selectedLanguage, brandedTitle])

  // Scroll button visibility
  const updateScrollButtons = useCallback(() => {
    if (scenariosScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scenariosScrollRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }, [])

  useEffect(() => {
    updateScrollButtons()
    const ref = scenariosScrollRef.current
    if (ref) {
      ref.addEventListener('scroll', updateScrollButtons)
      window.addEventListener('resize', updateScrollButtons)
    }
    return () => {
      if (ref) ref.removeEventListener('scroll', updateScrollButtons)
      window.removeEventListener('resize', updateScrollButtons)
    }
  }, [scenarios, selectedCategory, updateScrollButtons])

  // Scroll handler
  const scrollScenarios = (direction) => {
    if (scenariosScrollRef.current) {
      const cardWidth = 316
      scenariosScrollRef.current.scrollBy({
        left: direction === 'left' ? -cardWidth * 2 : cardWidth * 2,
        behavior: 'smooth',
      })
    }
  }

  // Avatar helpers
  const getInitials = (name) => {
    if (!name) return '?'
    const words = name.split(' ')
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const getAvatarColor = (name) => {
    const colors = [
      '#667eea', '#764ba2', '#2563eb', '#0891b2',
      '#059669', '#d97706', '#dc2626', '#db2777',
    ]
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  // Issue #435: scenario click opens the commitment sheet rather than
  // starting a run directly.
  //
  // #2335: this used to POST /api/runs immediately with a hard-coded
  // `modality: 'text'` and no confirmation — the only guest start path
  // that skipped the modality choice every other entry point gives
  // (CommitmentSheetOverlay, used by `/discover` and its J4 chooser flow).
  // This section only renders for `/for/:audience` partner pages (see the
  // audienceSlug-gated `#scenarios` section below) since #1922/#2098 hid
  // the gallery on the default homepage — so this click is now identical in
  // shape to `GuestChooserPage.jsx`'s `handleSelectCard`: it just opens
  // the SAME `CommitmentSheetOverlay` component with the chosen scenario,
  // deferring the actual run-creation POST to `handleCommitStart` below
  // once the guest picks a modality on the sheet.
  const handleScenarioClick = (scenario) => {
    if (!scenario || startingScenarioId) return
    setCommittingScenario(scenario)
  }

  // #2335: the run-creation call CommitmentSheet's primary CTA triggers,
  // carrying the modality the guest chose on the sheet (previously
  // hard-coded 'text' in handleScenarioClick above). Keeps the same
  // request shape (POST /api/runs, selectedLanguage, audienceSlug nav
  // state) handleScenarioClick used before this fix — only `modality` is
  // now a real choice instead of a literal.
  const handleCommitStart = async (modality) => {
    const scenario = committingScenario
    if (!scenario || startingScenarioId) return

    try {
      setStartingScenarioId(scenario.id)
      setStartError(null)

      // #3753: a guest from another org (e.g. a Yoma guest on this trial
      // home) gets a fresh guest; a same-org guest is kept (no-op).
      if (shouldEnsureGuestSession({ isAuthenticated, user })) {
        await loginAsGuest({ scenarioId: scenario.id })
      }

      const response = await safeAuthFetch(`${API_BASE_URL}/api/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenarioId: scenario.id,
          modality,
          language: selectedLanguage !== DEFAULT_LANGUAGE ? selectedLanguage : null,
        }),
      })
      const data = await response.json()
      const runId = data.run?.id || data.id
      if (!runId) throw new Error(data.error || 'Failed to start demo session')

      navigate(`/chat/${runId}?demo=true`, {
        state: audienceSlug ? { from: 'audience', audienceSlug, preferredMode: modality } : { preferredMode: modality }
      })
    } catch (err) {
      console.error('Failed to start demo:', err)
      // #3753 review round 2 (P3): REAL_SESSION_UNVERIFIED gets the same
      // message ScenarioDetailPage shows; anything else stays generic.
      setStartError(guestStartErrorMessage(err, t('errors.startFailed'), t))
      // #4724: close the start sheet, or it covers the startError alert and the click looks like it did nothing
      // (seen with /auth/guest at its per-IP rate limit).
      setCommittingScenario(null)
    } finally {
      setStartingScenarioId(null)
    }
  }

  // Filter scenarios by category (Workplace first, then alphabetical)
  // Content-keyed dedup — safe (not polled). Switch to ID-based if this becomes polled (#803).
  const categories = [...new Set(scenarios.map((s) => s.category).filter(Boolean))].sort((a, b) => {
    if (a === 'Workplace') return -1
    if (b === 'Workplace') return 1
    return a.localeCompare(b)
  })

  // Check if this audience has a framework visualisation
  const hasFramework = !!audienceConfig?.framework_config

  // Framework-aware scenario filtering
  // Translations are now applied server-side via ?lang= param (Issue #508).
  const filteredScenarios = useMemo(() => {
    if (hasFramework && selectedFrameworkArea) {
      return scenarios.filter((s) =>
        s.competence_code && s.competence_code.startsWith(selectedFrameworkArea + '.')
      )
    }
    if (selectedCategory === 'all') return scenarios
    return scenarios.filter((s) => s.category === selectedCategory)
  }, [hasFramework, selectedFrameworkArea, selectedCategory, scenarios])

  const scenarioCounts = categories.reduce((acc, cat) => {
    acc[cat] = scenarios.filter((s) => s.category === cat).length
    return acc
  }, {})

  // Issue #1926 (J19): the scenarios gallery only renders on /for/:audience
  // (see the `!!audienceSlug` gate below — #2098 hid it on the default
  // homepage). On an audience page, scroll to the existing #scenarios
  // anchor; on the default homepage there is nothing to scroll to, so this
  // sends the guest to the same /discover entry point the hero's own
  // secondary CTA and the B2B section's "try demo" link already use.
  const handleContinuityBrowse = useCallback(() => {
    if (audienceSlug) {
      const el = document.getElementById('scenarios')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        return
      }
    }
    navigate('/discover')
  }, [audienceSlug, navigate])

  return (
    <div className="home-page-v2">
      {/* #2098 call 2: persistent top-right Sign in — same !audienceSlug
          gate as GuestWelcomeHero below; /for/:audience keeps its own
          hero-embedded Sign in button and must never get a second one. */}
      {!audienceSlug && <GuestHomeHeader onSignIn={() => setShowLoginModal(true)} />}
      {/* #4724: /for/digcomp gets a top-left Tacit link home (Gilbert, human test 2026-10-29). Other audience
          pages are partner-branded and keep no Tacit header. */}
      {isDigComp && <DigCompHomeHeader />}

      {/* ========== HERO SECTION ========== */}
      <section className="v2-hero">
        {/* #1921 J1: the generic guest welcome ONLY replaces the DEFAULT
            (non-audience) homepage hero. /for/:audience pages keep their
            existing partner-branded hero untouched — audienceSlug is only
            ever set on that route (see the useParams() destructure above),
            never on the default "/" route this build targets. */}
        {audienceSlug ? (
          <div className="v2-hero-content">
            <h1 className="v2-hero-brand v2-fade-up">{brandName}</h1>
            {(orgBranding?.powered_by || branding.poweredBy) && (
              <p className="v2-hero-powered-by v2-fade-up" style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
                Powered by {orgBranding?.powered_by || branding.poweredBy}
              </p>
            )}
            <p className="v2-hero-tagline v2-fade-up v2-stagger-1">
              {/* #4724: digcomp copy comes from i18n directly, not via the
                  async audience row, so the hero's height (and the
                  #human-oversight anchor below it) never shifts on load. */}
              {isDigComp ? t('digcomp.heroTagline') : audienceConfig?.hero_tagline ? t(`${audienceSlug}.heroTagline`, { defaultValue: audienceConfig.hero_tagline }) : t('hero.tagline')}
            </p>
            <p className="v2-hero-subtitle v2-fade-up v2-stagger-2">
              {isDigComp ? t('digcomp.heroSubtitle') : audienceConfig?.hero_subtitle ? t(`${audienceSlug}.heroSubtitle`, { defaultValue: audienceConfig.hero_subtitle }) : t('hero.subtitle')}
            </p>
            {isDigComp ? (
              <>
                <div className="v2-hero-actions v2-fade-up v2-stagger-3">
                  <a href="#scenarios" className="v2-btn v2-btn-primary">
                    {t('digcomp.cop.heroPrimaryCta')}
                  </a>
                  <a href={`#${HUMAN_OVERSIGHT_ANCHOR}`} className="v2-btn v2-btn-secondary">
                    {t('digcomp.cop.heroSecondaryCta')}
                  </a>
                </div>
                {/* #4724: returning users keep a sign-in path without a second button. */}
                <button type="button" className="v2-hero-signin-link" onClick={() => setShowLoginModal(true)}>
                  {t('hero.signIn')}
                </button>
              </>
            ) : (
            <div className="v2-hero-actions v2-fade-up v2-stagger-3">
              <a href="#scenarios" className="v2-btn v2-btn-primary">
                {t('hero.tryDemo')}
              </a>
              <button
                className="v2-btn v2-btn-secondary"
                onClick={() => setShowLoginModal(true)}
              >
                {t('hero.signIn')}
              </button>
            </div>
            )}
          </div>
        ) : (
          <GuestWelcomeHero
            onFindConversation={() => posthog?.capture?.('discovery_cta_tapped', { source: 'homepage_hero' })}
            brandName={brandName}
          />
        )}
      </section>

      {/* Issue #1926 (J19, D-080 re-scope) — guest-only, gated on a fresh
          `last_run` token (§SCOPE NOTE: user?.is_guest is the canonical
          guest check). Renders on BOTH the default homepage and
          /for/:audience pages — J19's real-world traffic is dominated by
          the Yoma audience pathway (spec §D-080 Re-scope point 3), so this
          must not be gated on `!audienceSlug` the way GuestWelcomeHero is. */}
      {user?.is_guest && (
        <LastRunContinuity lastRun={lastRun} onBrowse={handleContinuityBrowse} />
      )}

      {/* #2497: the #1922/#2098 build below hides the entire scenarios
          gallery (categories, cards, language picker) on the default
          homepage — that removal was deliberate and measured (152 scenarios
          behind Browse-all, zero reachable only through it) and must stay.
          But it also hid the Create Your Own card, which the /discover
          guest-chooser funnel never grew a replacement for — that made the
          whole guest scenario-authoring flow unreachable from the default
          homepage. This standalone block restores just the card, not the
          gallery. audienceSlug is always empty here (default "/" route
          only; /for/:audience keeps its own copy of this card inside the
          gated section below), so hasFramework's intro copy never applies —
          matching the issue's "framework variant renders its own intro,
          both variants must show the entry point" requirement. */}
      {!audienceSlug && (
        <section className="v2-section v2-guest-create-section v2-fade-up">
          <div className="v2-container">
            <div className="v2-create-section">
              <button
                className="v2-create-card"
                onClick={() => setShowAuthoringDialog(true)}
              >
                <span className="v2-create-card-icon">+</span>
                <span>{t('scenarios.createYourOwn')}</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ========== SCENARIOS SECTION ==========
          #2098 call 5 addendum: hidden on the default homepage — both
          "Browse all" links that used to point at this #scenarios anchor
          are deleted (GuestWelcomeHero's secondary CTA,
          GuestDiscoveryContextsPage's footer link). MUST stay for
          /for/:audience partner pages — their hero's PRIMARY CTA is
          <a href="#scenarios">, so the gallery is the entire demo path
          there (measured 2026-08-08: 152 scenarios behind Browse-all, zero
          reachable only through it, on the default homepage's copy of this
          gallery — not this one).
          #2335: this section's own click handler used to hard-code
          `modality: 'text'` and start the run immediately with no
          confirmation — the only guest start path bypassing the modality
          choice every other entry point gives (CommitmentSheetOverlay).
          It now opens that same overlay (mounted once, near the other
          modals, below) instead of starting the run directly; the actual
          POST moved to `handleCommitStart`, fired from the sheet's own
          primary CTA once the guest picks type or voice. */}
      {(!!audienceSlug) && (
      <section id="scenarios" className="v2-scenarios-section">
        <div className="v2-container">
          <div className="v2-section-header v2-fade-up">
            <h2 className="v2-section-heading">
              {isDigComp ? t('digcomp.cop.wheelHeading') : hasFramework ? t('scenarios.headingFramework') : t('scenarios.heading')}
            </h2>
            <p className="v2-section-subtext">
              {isDigComp
                ? t('digcomp.cop.wheelSubtext')
                : hasFramework
                ? t('scenarios.subtextFramework')
                : t('scenarios.subtext')}
            </p>
            <div className="v2-language-selector">
              <div className="v2-language-selector-row">
                <span className="v2-language-globe" aria-hidden="true">&#127760;</span>
                <label htmlFor="language-select" className="v2-language-label">{t('language.practiceIn')}</label>
                <select
                  id="language-select"
                  className="v2-language-select"
                  value={selectedLanguage}
                  onChange={(e) => {
                    setSelectedLanguage(e.target.value)
                    setPreferredLanguage(e.target.value)
                    i18n.changeLanguage(e.target.value)
                  }}
                >
                  {LANGUAGE_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.codes.map((code) => {
                        const lang = getLanguageConfig(code)
                        return lang ? (
                          <option key={`${group.label}-${code}`} value={code}>{lang.name}</option>
                        ) : null
                      })}
                    </optgroup>
                  ))}
                </select>
              </div>
              <span className="v2-language-hint">
                {selectedLanguage !== DEFAULT_LANGUAGE
                  ? t('language.conversationIn', { language: SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.name })
                  : t('language.chooseFrom', { count: SUPPORTED_LANGUAGES.length })}
              </span>
            </div>
          </div>

          {startError && <div className="v2-error-message">{startError}</div>}

          {isLoading ? (
            <div className="v2-scenarios-loading">{t('scenarios.loading')}</div>
          ) : (
            <>
              {/* Framework visual (e.g., DigComp pentagon) or category pills */}
              {hasFramework ? (
                <div className="v2-fade-up v2-stagger-1">
                  <DigCompWheel
                    frameworkConfig={audienceConfig.framework_config}
                    scenarios={scenarios}
                    onSelectArea={setSelectedFrameworkArea}
                    onStartScenario={handleScenarioClick}
                    selectedAreaId={selectedFrameworkArea}
                    loadingScenarioId={startingScenarioId}
                    deepLinkScenarioId={deepLinkScenarioId}
                  />
                </div>
              ) : (
                <div className="v2-fade-up v2-stagger-1">
                  <CategoryIconCards
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onSelect={(cat) => {
                      setSelectedCategory(cat)
                      try {
                        localStorage.setItem('homepage_selected_category', cat)
                      } catch {
                        // Ignore
                      }
                    }}
                    scenarioCounts={scenarioCounts}
                    totalCount={scenarios.length}
                  />
                </div>
              )}

              {/* Standard scenario card gallery — only for non-framework audiences */}
              {!hasFramework && (
              <div className="v2-scenarios-scroll-wrapper v2-fade-up v2-stagger-2">
                {canScrollLeft && (
                  <button
                    className="v2-scroll-btn v2-scroll-btn-left"
                    onClick={() => scrollScenarios('left')}
                    aria-label="Scroll left"
                  >
                    &#8249;
                  </button>
                )}

                <div className="v2-scenarios-gallery" ref={scenariosScrollRef}>
                  {/* Scenario Cards */}
                  {filteredScenarios.map((scenario, index) => {
                    const isThisLoading = startingScenarioId === scenario.id
                    const showBadge = index === 0

                    return (
                      <button
                        key={scenario.id}
                        className={`v2-scenario-card ${isThisLoading ? 'loading' : ''}`}
                        onClick={() => handleScenarioClick(scenario)}
                        disabled={!!startingScenarioId}
                      >
                        {showBadge && (
                          <span className="v2-scenario-badge">{t('scenarios.startHere')}</span>
                        )}
                        {isThisLoading && (
                          <div className="v2-scenario-loading-overlay">
                            <div className="v2-loading-spinner" />
                          </div>
                        )}
                        <div className="v2-scenario-card-header">
                          {scenario.avatar_url ? (
                            <img
                              src={scenario.avatar_url}
                              alt={scenario.ai_role || scenario.name}
                              className="v2-scenario-avatar"
                            />
                          ) : (
                            <div
                              className="v2-scenario-avatar-placeholder"
                              style={{
                                backgroundColor: getAvatarColor(
                                  scenario.ai_role || scenario.name
                                ),
                              }}
                            >
                              {getInitials(scenario.ai_role || scenario.name)}
                            </div>
                          )}
                          <h3>{scenario.name}</h3>
                        </div>
                        <p className="v2-scenario-description">{scenario.summary}</p>
                      </button>
                    )
                  })}
                </div>

                {canScrollRight && (
                  <button
                    className="v2-scroll-btn v2-scroll-btn-right"
                    onClick={() => scrollScenarios('right')}
                    aria-label="Scroll right"
                  >
                    &#8250;
                  </button>
                )}
              </div>
              )}

              {/* Create Your Own — with intro copy for framework audiences */}
              <div className={`v2-create-section ${hasFramework ? 'v2-create-section--framework' : ''} v2-fade-up v2-stagger-3`}>
                {hasFramework && (
                  <div className="v2-create-section__intro">
                    <h3 className="v2-create-section__heading">{t('scenarios.beyondFramework')}</h3>
                    <p className="v2-create-section__text">
                      {t('scenarios.beyondFrameworkText')}
                    </p>
                  </div>
                )}
                <button
                  className="v2-create-card"
                  onClick={() => setShowAuthoringDialog(true)}
                  disabled={!!startingScenarioId}
                >
                  <span className="v2-create-card-icon">+</span>
                  <span>{t('scenarios.createYourOwn')}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </section>
      )}

      {/* #4724: featured human-oversight scenarios, one example of the mapped set, so they sit BELOW the
          wheel (Gilbert, human test 2026-10-29). The wheel above loads late, so the forum-linked
          #human-oversight landing is re-applied as it grows (see the landing effects above). Same start
          flow as the wheel cards (CommitmentSheetOverlay via handleScenarioClick -> handleCommitStart),
          so guests need no account. */}
      {isDigComp && (
        <HumanOversightSection
          scenarios={scenarios}
          isLoading={isLoading}
          loadError={loadError}
          startError={startError}
          outcomeMap={digcompOutcomeMap}
          onStartScenario={handleScenarioClick}
          loadingScenarioId={startingScenarioId}
        />
      )}

      {/* #4724: on /for/digcomp the generic features and B2B blocks are
          replaced by the DigComp mapping, CoP and contact sections. */}
      {isDigComp && (
        <>
          <HowWeMapSection />
          <FromCopSection />
          <DigCompContactSection onSignIn={() => setShowLoginModal(true)} />
        </>
      )}

      {/* ========== FEATURES SECTION ========== */}
      {!isDigComp && (
      <section className="v2-features-section v2-section">
        <div className="v2-container">
          <div className="v2-section-header">
            <h2 className="v2-section-heading v2-fade-up">{t('features.heading', { appName: productBrandName }).replace('Tacit', productBrandName)}</h2>
            <p className="v2-section-subtext v2-fade-up v2-stagger-1">
              {t('features.subtext')}
            </p>
          </div>

          <div className="v2-features-grid" ref={featuresGridRef}>
            <div className="v2-feature-tile wide v2-fade-up v2-stagger-2">
              <div className="v2-feature-caption">
                <h3>{t('features.voiceMode')}</h3>
                <p>{t('features.voiceModeDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <video src="/images/features/video.mp4" autoPlay loop muted playsInline />
              </div>
            </div>

            <div className="v2-feature-tile wide v2-fade-up v2-stagger-3">
              <div className="v2-feature-caption">
                <h3>{t('features.aiAuthored')}</h3>
                <p>{t('features.aiAuthoredDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <video src="/images/features/ai-authoring.mp4" autoPlay loop muted playsInline />
              </div>
            </div>

            <div className="v2-feature-tile wide v2-fade-up v2-stagger-4">
              <div className="v2-feature-caption">
                <h3>{t('features.instantFeedback')}</h3>
                <p>{t('features.instantFeedbackDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <img src="/images/features/feedback2.png" alt={t('features.instantFeedback')} />
              </div>
            </div>

            <div className="v2-feature-tile wide v2-fade-up v2-stagger-5">
              <div className="v2-feature-caption">
                <h3>{t('features.skillsCoach')}</h3>
                <p>{t('features.skillsCoachDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <video src="/images/features/coaching.mp4" autoPlay loop muted playsInline />
              </div>
            </div>

            <div className="v2-feature-tile wide v2-fade-up v2-stagger-6">
              <div className="v2-feature-caption">
                <h3>{t('features.focusMode')}</h3>
                <p>{t('features.focusModeDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <img src="/images/features/focus.png" alt={t('features.focusMode')} />
              </div>
            </div>

            <div className="v2-feature-tile wide v2-fade-up v2-stagger-7">
              <div className="v2-feature-caption">
                <h3>{t('features.textChat')}</h3>
                <p>{t('features.textChatDesc')}</p>
              </div>
              <div className="v2-feature-visual">
                <video src="/images/features/text.mp4" autoPlay loop muted playsInline />
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ========== B2B SECTION ========== */}
      {!isDigComp && (
      <section className="v2-b2b-section">
        <div className="v2-container">
          <div className="v2-section-header">
            <h2 className="v2-section-heading v2-fade-up">{t('b2b.heading')}</h2>
            <p className="v2-section-subtext v2-fade-up v2-stagger-1">
              {t('b2b.subtext')}
            </p>
          </div>

          <div className="v2-b2b-grid">
            <div className="v2-b2b-card v2-fade-up v2-stagger-2">
              <div className="v2-b2b-icon">&#127912;</div>
              <h3>{t('b2b.customScenarios')}</h3>
              <p>{t('b2b.customScenariosDesc')}</p>
            </div>
            <div className="v2-b2b-card v2-fade-up v2-stagger-3">
              <div className="v2-b2b-icon">&#128202;</div>
              <h3>{t('b2b.progressTracking')}</h3>
              <p>{t('b2b.progressTrackingDesc')}</p>
            </div>
            <div className="v2-b2b-card v2-fade-up v2-stagger-4">
              <div className="v2-b2b-icon">&#128101;</div>
              <h3>{t('b2b.teamManagement')}</h3>
              <p>{t('b2b.teamManagementDesc')}</p>
            </div>
            <div className="v2-b2b-card v2-fade-up v2-stagger-5">
              <div className="v2-b2b-icon">&#129513;</div>
              <h3>{t('b2b.lmsIntegration')}</h3>
              <p>{t('b2b.lmsIntegrationDesc')}</p>
            </div>
          </div>

          <div className="v2-b2b-actions v2-fade-up v2-stagger-6">
            {/* #2098 call 5 collateral: #scenarios no longer exists on the
                default homepage (hidden above), so this CTA would silently
                dead-link there. /for/:audience keeps the gallery, so its
                anchor is unchanged; the default homepage's equivalent "try
                it" destination is now /discover, same as the hero CTA. */}
            {audienceSlug ? (
              <a href="#scenarios" className="v2-btn v2-btn-secondary">
                {t('b2b.tryDemo')}
              </a>
            ) : (
              <Link to="/discover" className="v2-btn v2-btn-secondary">
                {t('b2b.tryDemo')}
              </Link>
            )}
            <a
              href="mailto:hello@tacit.coach?subject=Inquiry"
              className="v2-btn v2-btn-primary"
            >
              {t('b2b.contactUs')}
            </a>
          </div>
        </div>
      </section>
      )}

      {/* ========== FOOTER ========== */}
      <footer className="v2-footer">
        <div className="v2-container">
          <div className="v2-footer-content">
            <div className="v2-footer-links">
              <Link to="/terms">{t('footer.terms')}</Link>
              <Link to="/privacy">{t('footer.privacy')}</Link>
            </div>
            <p className="v2-footer-copyright">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>

      {/* ========== MODALS ========== */}
      <AuthoringDemoDialog
        isOpen={showAuthoringDialog}
        onClose={() => setShowAuthoringDialog(false)}
      />

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      {/* #2335: same CommitmentSheetOverlay the /discover guest paths use —
          gives partner-page (`/for/:audience`) gallery starts the modality
          choice + prep visibility every other guest start path already has,
          instead of the old direct-to-'text' handleScenarioClick POST. */}
      <CommitmentSheetOverlay
        scenario={committingScenario}
        isStarting={!!startingScenarioId}
        onDismiss={() => { setCommittingScenario(null); setStartError(null) }}
        onStart={handleCommitStart}
      />
    </div>
  )
}

export default HomePageV2
